import React from 'react'
import { Heading } from '@astryxdesign/core/Heading'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { Token } from '@astryxdesign/core/Token'
import BookCover from './BookCover'
import StarRating from './StarRating'
import { publishedStoryUrl } from './publishedStoryLinks'

const GENRE_COLORS = ['blue', 'purple', 'teal', 'orange', 'pink'] as const

interface PublishedStoryCardProps {
  story: PublishedStorySummary
  index: number
  /** Rendered in the trailing column, e.g. an unsave button on the library page. */
  actions?: React.ReactNode
}

/**
 * One published story as a feed row. Extracted from HomeView so the library page and both
 * author-profile grids render the identical card rather than three near-copies.
 *
 * The trailing column used to hold a decorative bookmark icon that looked like a control and did
 * nothing; it now shows the story's rating and save count, and the real save control lives on the
 * story page itself.
 */
export default function PublishedStoryCard({
  story,
  index,
  actions
}: PublishedStoryCardProps): React.JSX.Element {
  const genres = String(story.genre || '')
    .split(',')
    .map((genre) => genre.trim())
    .filter(Boolean)

  return (
    <article className="home-feed-story">
      <a
        href={publishedStoryUrl(story)}
        className="home-story-link"
        aria-label={`Read ${story.title}`}
      >
        <HStack gap={5} align="stretch">
          <BookCover
            size="sm"
            coverUrl={story.coverUrl}
            title={story.title}
            authorName={story.authorName}
            placeholderIndex={index}
            className="home-story-cover-wide"
          />
          <VStack gap={1} className="home-story-copy">
            <Heading level={3}>{story.title}</Heading>
            <Text type="supporting">
              {story.authorName} <span aria-hidden="true">·</span> {story.readingTimeMinutes} min
              read
            </Text>
            <Text color="secondary" maxLines={2}>
              {story.blurb}
            </Text>
            {genres.length > 0 && (
              <HStack gap={1} wrap="wrap" className="home-story-tags">
                {genres.slice(0, 3).map((genre, genreIndex) => (
                  <Token
                    key={genre}
                    label={genre}
                    size="sm"
                    color={GENRE_COLORS[genreIndex % GENRE_COLORS.length]}
                  />
                ))}
              </HStack>
            )}
          </VStack>
          <VStack justify="between" align="end" className="home-story-actions">
            {story.ratingCount > 0 ? (
              <HStack gap={1} align="center">
                <StarRating value={Math.round(story.ratingAverage ?? 0)} size={14} />
                <Text type="supporting">{story.ratingAverage}</Text>
              </HStack>
            ) : (
              <span />
            )}
            <Text type="supporting">
              {story.saveCount > 0
                ? `${story.saveCount} save${story.saveCount === 1 ? '' : 's'}`
                : `${story.readingTimeMinutes} min read`}
            </Text>
          </VStack>
        </HStack>
      </a>
      {actions}
    </article>
  )
}
